/**
 * Browser-side numerology engine.
 *
 * The OCaml calculation library (numerology-unikernel/lib) is compiled to
 * JavaScript with js_of_ocaml and lazy-loaded on first use. It exposes the
 * exact same endpoint surface as the HTTP API, so results are byte-for-byte
 * identical to the server, but instant and available offline.
 *
 * Rebuild with: npm run build:engine
 */

import { apiLogger } from './logger';

interface NumerologyEngine {
  calculate(path: string, body: string): string;
}

declare global {
  var NumerologyEngine: NumerologyEngine | undefined;
}

let enginePromise: Promise<NumerologyEngine | null> | null = null;

export function loadEngine(): Promise<NumerologyEngine | null> {
  if (!enginePromise) {
    enginePromise = import('@/vendor/numerology-engine.js')
      .then((mod: Record<string, unknown>) => {
        // js_of_ocaml exports to module.exports when bundler CJS interop
        // provides one, otherwise to globalThis
        const fromModule =
          (mod?.NumerologyEngine as NumerologyEngine | undefined) ??
          ((mod?.default as Record<string, unknown> | undefined)
            ?.NumerologyEngine as NumerologyEngine | undefined);
        const engine = fromModule ?? globalThis.NumerologyEngine ?? null;
        if (engine) {
          apiLogger.debug({ msg: 'Numerology engine loaded (browser-side OCaml)' });
        } else {
          apiLogger.error({ msg: 'Numerology engine script loaded but export missing' });
        }
        return engine;
      })
      .catch((error) => {
        apiLogger.error({ msg: 'Failed to load numerology engine', error });
        return null;
      });
  }
  return enginePromise;
}

export async function engineRequest<T = unknown>(
  endpoint: string,
  body: unknown
): Promise<T> {
  const engine = await loadEngine();
  if (!engine) {
    throw new Error('Engine unavailable');
  }
  const raw = engine.calculate(endpoint, JSON.stringify(body ?? {}));
  const result = JSON.parse(raw);
  if (result && typeof result === 'object' && 'error' in result && result.error) {
    throw new Error(String(result.error));
  }
  return result as T;
}

export async function isEngineAvailable(): Promise<boolean> {
  return (await loadEngine()) !== null;
}
